// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import * as Sentry from '@sentry/react';
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { getUserFacingErrorMessage, handleAPIError } from '@/api/error-utils';
import { deleteDraftInvoice } from '@/billing/api/delete-draft-invoice';
import { invalidateInvoiceQueries } from '@/billing/hooks/invalidate-invoice-queries';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

// Codes the caller handles explicitly (known status errors routed via the
// known-error snackbar). Skip Sentry for these.
const EXPECTED_ERROR_CODES = new Set<number>([
  ERROR_CODE_MAPPING.invoice_not_deletable,
]);

interface UseDeleteDraftInvoiceOptions {
  invoiceKey: string;
  onSuccess?: () => void;
  onError?: (
    message: string,
    apiStatusCode: number,
    httpStatusCode: number
  ) => void;
}

export function useDeleteDraftInvoice(options: UseDeleteDraftInvoiceOptions) {
  const queryClient = useQueryClient();

  const invalidateAll = () =>
    invalidateInvoiceQueries({
      queryClient,
      invoiceKey: options.invoiceKey,
      includeCharges: true,
    });

  return useMutation({
    mutationFn: () => deleteDraftInvoice(options.invoiceKey),
    onSuccess: async () => {
      await invalidateAll();
      options.onSuccess?.();
    },
    onError: async (error: AxiosError) => {
      const apiError = handleAPIError(error);
      await invalidateAll();
      if (
        !apiError.sentToSentry &&
        !EXPECTED_ERROR_CODES.has(apiError.apiStatusCode)
      ) {
        Sentry.captureException(error);
      }
      options.onError?.(
        getUserFacingErrorMessage(apiError),
        apiError.apiStatusCode,
        apiError.httpStatusCode
      );
    },
  });
}
